import { C } from "./brandTokens";

export type OrnamentVariant = "hairline" | "asterism" | "leaf" | "arc";

interface OrnamentProps {
  variant?: OrnamentVariant;
  width?: number;
  color?: string;
  className?: string;
}

// Single-detail decorative SVGs. Swiss-editorial / NYRB restraint:
// one small mark per section at most. Muted ink tones, no animation.
export default function Ornament({
  variant = "hairline",
  width = 64,
  color = C.txt3,
  className,
}: OrnamentProps) {
  const stroke = color;

  if (variant === "hairline") {
    // A thin rule with a centered, 45°-rotated square. Evokes a book fleuron.
    return (
      <svg
        viewBox={`0 0 ${width} 10`}
        width={width}
        height={10}
        className={className}
        role="presentation"
        aria-hidden="true"
      >
        <line
          x1={0}
          y1={5}
          x2={width / 2 - 6}
          y2={5}
          stroke={stroke}
          strokeWidth={0.75}
          opacity={0.55}
        />
        <rect
          x={width / 2 - 3}
          y={2}
          width={6}
          height={6}
          transform={`rotate(45 ${width / 2} 5)`}
          fill="none"
          stroke={stroke}
          strokeWidth={0.75}
          opacity={0.7}
        />
        <line
          x1={width / 2 + 6}
          y1={5}
          x2={width}
          y2={5}
          stroke={stroke}
          strokeWidth={0.75}
          opacity={0.55}
        />
      </svg>
    );
  }

  if (variant === "asterism") {
    // Three dots in a triangle — the ⁂ mark used in literary typography
    // to signal a pause between passages.
    const s = 3;
    return (
      <svg
        viewBox="0 0 40 20"
        width={width}
        height={width * 0.5}
        className={className}
        role="presentation"
        aria-hidden="true"
      >
        <circle cx={20} cy={5} r={s / 2} fill={stroke} opacity={0.7} />
        <circle cx={13} cy={15} r={s / 2} fill={stroke} opacity={0.7} />
        <circle cx={27} cy={15} r={s / 2} fill={stroke} opacity={0.7} />
      </svg>
    );
  }

  if (variant === "leaf") {
    // Single elongated olive leaf, centered on its stem.
    return (
      <svg
        viewBox="0 0 40 20"
        width={width}
        height={width * 0.5}
        className={className}
        role="presentation"
        aria-hidden="true"
      >
        <path
          d="M 4 10 Q 20 2, 36 10 Q 20 18, 4 10 Z"
          fill="none"
          stroke={stroke}
          strokeWidth={0.9}
          opacity={0.65}
        />
        <line
          x1={4}
          y1={10}
          x2={36}
          y2={10}
          stroke={stroke}
          strokeWidth={0.6}
          opacity={0.45}
        />
      </svg>
    );
  }

  // arc — a half-circle with a dot beneath. Reads as "sunrise" without being literal.
  return (
    <svg
      viewBox="0 0 40 20"
      width={width}
      height={width * 0.5}
      className={className}
      role="presentation"
      aria-hidden="true"
    >
      <path
        d="M 6 16 A 14 14 0 0 1 34 16"
        fill="none"
        stroke={stroke}
        strokeWidth={0.9}
        opacity={0.6}
      />
      <circle cx={20} cy={17.5} r={0.9} fill={stroke} opacity={0.75} />
    </svg>
  );
}
